import React, { useRef } from "react";
import { BASIC_SHAPES, TEMPLATES, ANIMATION_PRESETS } from "./templates";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import LottiePanel from "./LottiePanel";
import {
  Square, Circle, Minus, Triangle, Star, Type, Wand2,
  LoaderCircle, BadgeCheck, AudioLines, PanelTop, Sparkles,
  Hexagon, Pentagon, Heart, ArrowRight, Orbit, Radio, Film, Library, X,
  Loader, CircleDot, Gauge, Cog, Target, ImagePlus, Upload,
} from "lucide-react";

const SHAPE_ICONS = {
  rect: Square, circle: Circle, ellipse: Circle, line: Minus,
  polygon: Triangle, star: Star, hexagon: Hexagon, pentagon: Pentagon,
  heart: Heart, arrow: ArrowRight, text: Type,
};
const TEMPLATE_ICONS = {
  "loader-dots": LoaderCircle,
  "spinner-arc": LoaderCircle,
  "dual-ring": Loader,
  "pulse-ring": Radio,
  "bouncing-balls": CircleDot,
  "progress-bar": Gauge,
  "gradient-orb": Orbit,
  "orbit-loader": Orbit,
  "gear-spin": Cog,
  "concentric-rotate": Target,
  "badge-check": BadgeCheck,
  "heartbeat": Heart,
  "wave-bars": AudioLines,
  "card-banner": PanelTop,
};

// Static, render-stable values hoisted out of the component.
const ACCORDION_DEFAULT = ["shapes", "templates", "animations", "lottie"];

function buildTilePayload(kind, itemId) {
  if (kind === "shape") return { kind, shapeId: itemId };
  if (kind === "template") return { kind, templateId: itemId };
  return { kind, presetId: itemId };
}

function DraggableTile({ kind, itemId, label, icon: Icon, testId, onDragStartExtra }) {
  const onDragStart = (e) => {
    e.dataTransfer.setData(
      "application/x-svg-studio",
      JSON.stringify(buildTilePayload(kind, itemId))
    );
    e.dataTransfer.effectAllowed = "copy";
    onDragStartExtra?.();
  };
  return (
    <div
      draggable
      onDragStart={onDragStart}
      data-testid={testId}
      className="shape-card group aspect-square border border-line bg-panel-soft hover:border-[#002FA7] hover:bg-hover flex flex-col items-center justify-center gap-2 cursor-grab text-ink3 hover:text-ink transition-colors"
    >
      <Icon className="h-5 w-5" strokeWidth={1.5} />
      <span className="text-[10px] font-mono uppercase tracking-wider">{label}</span>
    </div>
  );
}

function AnimationTile({ preset, onApply, onDragStartExtra }) {
  const onDragStart = (e) => {
    e.dataTransfer.setData(
      "application/x-svg-studio",
      JSON.stringify(buildTilePayload("animation", preset.id))
    );
    onDragStartExtra?.();
  };
  return (
    <button
      draggable
      onDragStart={onDragStart}
      onClick={() => onApply(preset)}
      data-testid={`anim-preset-${preset.id}-btn`}
      className="border border-line bg-panel-soft hover:border-[#002FA7] hover:text-ink text-ink3 py-2 text-xs font-mono uppercase tracking-wider flex items-center gap-2 px-3 transition-colors"
    >
      <Sparkles className="h-3 w-3" />
      {preset.label}
    </button>
  );
}

export default function LeftPanel({ onApplyAnimation, onAddImage, isCompact, onClose, onTileDragStart }) {
  const imageInputRef = useRef(null);

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (file) onAddImage?.(file);
    e.target.value = "";
  };

  return (
    <aside className="floating-panel w-full h-full flex flex-col overflow-hidden">
      <div className="h-12 border-b border-line flex items-center justify-between px-4 text-[11px] font-mono uppercase tracking-[0.2em] text-ink4 shrink-0">
        <span className="flex items-center gap-2"><Library className="h-3.5 w-3.5 text-[#FFB400]" /> Library</span>
        {isCompact && (
          <button onClick={onClose} data-testid="left-panel-close-btn" className="h-7 w-7 flex items-center justify-center text-ink4 hover:text-ink hover:bg-hover transition-colors -mr-2" title="Close">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto scroll-thin">
        <Accordion type="multiple" defaultValue={ACCORDION_DEFAULT} className="w-full">
        <AccordionItem value="shapes" className="border-b border-line">
          <AccordionTrigger className="px-4 text-xs font-mono uppercase tracking-wider text-ink2 hover:bg-hover hover:no-underline">
            Basic Shapes
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3">
            <div className="grid grid-cols-3 gap-2">
              {BASIC_SHAPES.map((s) => (
                <DraggableTile
                  key={s.id}
                  kind="shape"
                  itemId={s.id}
                  label={s.label}
                  icon={SHAPE_ICONS[s.id] || Square}
                  testId={`shape-${s.id}-tile`}
                  onDragStartExtra={onTileDragStart}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="image" className="border-b border-line">
          <AccordionTrigger className="px-4 text-xs font-mono uppercase tracking-wider text-ink2 hover:bg-hover hover:no-underline">
            <span className="flex items-center gap-2">
              <ImagePlus className="h-3.5 w-3.5 text-[#002FA7]" />
              Image
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3">
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              data-testid="upload-image-btn"
              className="w-full border border-dashed border-line bg-panel-soft hover:border-[#002FA7] hover:bg-hover text-ink3 hover:text-ink py-4 flex flex-col items-center gap-2 transition-colors"
            >
              <Upload className="h-5 w-5" />
              <span className="text-[10px] font-mono uppercase tracking-wider">上传图片 · Upload</span>
            </button>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImagePick}
            />
            <p className="text-[10px] text-ink4 mt-2 font-mono leading-relaxed">
              也可直接拖拽图片到画布，或 Ctrl/Cmd+V 粘贴
            </p>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="templates" className="border-b border-line">
          <AccordionTrigger className="px-4 text-xs font-mono uppercase tracking-wider text-ink2 hover:bg-hover hover:no-underline">
            Templates
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3">
            <div className="grid grid-cols-2 gap-2">
              {TEMPLATES.map((t) => (
                <DraggableTile
                  key={t.id}
                  kind="template"
                  itemId={t.id}
                  label={t.label.replace(/^.+·\s*/, "")}
                  icon={TEMPLATE_ICONS[t.id] || Wand2}
                  testId={`template-${t.id}-tile`}
                  onDragStartExtra={onTileDragStart}
                />
              ))}
            </div>
            <p className="text-[10px] text-ink4 mt-3 font-mono">DRAG TO CANVAS</p>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="animations" className="border-b border-line">
          <AccordionTrigger className="px-4 text-xs font-mono uppercase tracking-wider text-ink2 hover:bg-hover hover:no-underline">
            Animations
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3">
            <div className="flex flex-col gap-1.5">
              {ANIMATION_PRESETS.map((p) => (
                <AnimationTile key={p.id} preset={p} onApply={onApplyAnimation} onDragStartExtra={onTileDragStart} />
              ))}
            </div>
            <p className="text-[10px] text-ink4 mt-3 font-mono">
              SELECT AN ELEMENT, THEN CLICK A PRESET
            </p>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="lottie" className="border-b border-line">
          <AccordionTrigger className="px-4 text-xs font-mono uppercase tracking-wider text-ink2 hover:bg-hover hover:no-underline">
            <span className="flex items-center gap-2">
              <Film className="h-3.5 w-3.5" />
              Lottie Preview
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3">
            <LottiePanel />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      </div>
    </aside>
  );
}
